/**
 * First-touch marketing attribution for the current browser session.
 *
 * Captured once on app load from the landing URL (utm_*, Mailchimp mc_cid /
 * mc_eid, gclid, fbclid) plus referrer and landing page, kept in
 * sessionStorage, and sent with checkout as `utm_data` so connected
 * integrations can credit the campaign (Mailchimp's campaign_id, Klaviyo's
 * attribution, GA4 later).
 */

export const ATTRIBUTION_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'mc_cid', 'mc_eid', 'gclid', 'fbclid'] as const;

const STORAGE_KEY = 'rumio_attribution';

export type Attribution = Partial<Record<(typeof ATTRIBUTION_KEYS)[number], string>> & {
  landing_page?: string;
  referrer?: string;
  captured_at?: string;
  /** Meta Pixel browser id / click id, added at read time for the Conversions API. */
  fbp?: string;
  fbc?: string;
};

function read(): Attribution | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Attribution) : null;
  } catch {
    return null;
  }
}

function write(value: Attribution): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    // private mode / blocked storage: attribution is best-effort
  }
}

/**
 * Record attribution from the current URL. First touch wins for campaign
 * params within a session; a later visit with NEW params replaces them
 * (the shopper clicked a different campaign link).
 */
export function captureAttribution(search: string = typeof window !== 'undefined' ? window.location.search : '', referrer: string = typeof document !== 'undefined' ? document.referrer : ''): Attribution | null {
  if (typeof window === 'undefined') return null;
  const params = new URLSearchParams(search);
  const found: Attribution = {};
  for (const key of ATTRIBUTION_KEYS) {
    const v = params.get(key);
    if (v && v.length <= 200) found[key] = v;
  }
  const existing = read();
  if (Object.keys(found).length === 0) {
    if (existing) return existing;
    const first: Attribution = {
      landing_page: window.location.pathname + window.location.search,
      referrer: referrer && !referrer.startsWith(window.location.origin) ? referrer.slice(0, 500) : undefined,
      captured_at: new Date().toISOString(),
    };
    if (!first.referrer) delete first.referrer;
    write(first);
    return first;
  }
  const next: Attribution = {
    ...found,
    landing_page: window.location.pathname + window.location.search,
    referrer: referrer && !referrer.startsWith(window.location.origin) ? referrer.slice(0, 500) : existing?.referrer,
    captured_at: new Date().toISOString(),
  };
  if (!next.referrer) delete next.referrer;
  write(next);
  return next;
}

function cookie(name: string): string | undefined {
  if (typeof document === 'undefined') return undefined;
  const m = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return m ? decodeURIComponent(m[1]) : undefined;
}

/**
 * Meta click identifiers for the Conversions API: `_fbp` / `_fbc` are set by
 * the Pixel; without the `_fbc` cookie it is derived from the landing fbclid.
 */
export function metaClickIds(a: Attribution | null): { fbp?: string; fbc?: string } {
  const out: { fbp?: string; fbc?: string } = {};
  const fbp = cookie('_fbp');
  if (fbp && fbp.length <= 100) out.fbp = fbp;
  const fbc = cookie('_fbc');
  if (fbc && fbc.length <= 300) out.fbc = fbc;
  else if (a?.fbclid) {
    const ts = a.captured_at ? Date.parse(a.captured_at) : Date.now();
    out.fbc = `fb.1.${Number.isFinite(ts) ? ts : Date.now()}.${a.fbclid}`;
  }
  return out;
}

/** What checkout sends as `utm_data`; undefined when nothing was captured. */
export function getAttribution(): Attribution | undefined {
  const a = read();
  const ids = metaClickIds(a);
  if (!a) return Object.keys(ids).length ? (ids as Attribution) : undefined;
  const { captured_at: _c, ...rest } = a;
  const merged = { ...rest, ...ids };
  return Object.keys(merged).length ? (merged as Attribution) : undefined;
}

export function _clearAttributionForTests(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* noop */
  }
}
